import { axiosPrivate } from "./axios";
import { uploadFile, deleteFile, IMAGES_BUCKET } from "./supabaseStorage";

/**
 * Unified Task Board API (/api/task-board).
 * Independent of the legacy admin/team boards in taskService.js.
 * Every function returns `response.data` ({ success, data, meta?, message? }).
 */

const BASE = "/api/task-board";

/** Drop empty filter values so the query string only carries active filters. */
const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && !v.length),
    ).map(([k, v]) => [k, Array.isArray(v) ? v.join(",") : v]),
  );

export const boardService = {
  getOptions: async () => (await axiosPrivate.get(`${BASE}/options`)).data,
};

export const boardTaskService = {
  list: async (filters) =>
    (await axiosPrivate.get(`${BASE}/tasks`, { params: cleanParams(filters) })).data,
  get: async (id) => (await axiosPrivate.get(`${BASE}/tasks/${id}`)).data,
  create: async (task) => (await axiosPrivate.post(`${BASE}/tasks`, task)).data,
  /** Partial update; pass `version` to get a 409 instead of overwriting a newer change. */
  update: async (id, changes) => (await axiosPrivate.patch(`${BASE}/tasks/${id}`, changes)).data,
  /** Move a card to `status`, optionally at a place in the column (`position`). */
  move: async (id, status, position) =>
    (
      await axiosPrivate.patch(
        `${BASE}/tasks/${id}/status`,
        position === undefined ? { status } : { status, position },
      )
    ).data,
  /** Permanent delete (high_potit only). */
  remove: async (id) => (await axiosPrivate.delete(`${BASE}/tasks/${id}`)).data,
  /** Wipe the task's QA-rejection record (high_potit only). Returns the updated task. */
  clearQaRejections: async (id) => (await axiosPrivate.delete(`${BASE}/tasks/${id}/qa-rejections`)).data,
};

/**
 * Task comments, activity feed and attachments. Files are never public: they
 * are fetched through the authorized API as blobs.
 */
// Task and comment files go to the public Supabase bucket like the rest of
// the app; the API stores only their metadata and URL.
export const bucketPath = (url) => {
  const marker = `/object/public/${IMAGES_BUCKET}/`;
  const i = (url || "").indexOf(marker);
  return i === -1 ? null : decodeURIComponent(url.slice(i + marker.length).split("?")[0]);
};

/**
 * Upload `files` to Supabase, then call `save(metadata)`. Progress is reported
 * as `{loaded, total}` (files done / files). If saving fails the uploaded
 * files are removed again.
 */
const withUploadedFiles = async (taskId, files, onProgress, save) => {
  const meta = [];
  try {
    for (const file of files) {
      onProgress?.({ loaded: meta.length, total: files.length });
      const safeName = file.name.replace(/[^\w.-]+/g, "_");
      const path = `tasks/${taskId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${safeName}`;
      const { publicUrl, error } = await uploadFile(IMAGES_BUCKET, path, file);
      if (error || !publicUrl) throw new Error(`${file.name}: ${error?.message || "upload failed"}`);
      meta.push({ name: file.name, url: publicUrl, size: file.size, contentType: file.type, path });
    }
    if (files.length) onProgress?.({ loaded: files.length, total: files.length });
    return await save(meta.map(({ path, ...m }) => m));
  } catch (err) {
    meta.forEach((m) => deleteFile(IMAGES_BUCKET, m.path).catch(() => {}));
    throw err;
  }
};

export const taskDiscussionService = {
  activity: async (taskId, { filter = "all", before, limit } = {}) =>
    (
      await axiosPrivate.get(`${BASE}/tasks/${taskId}/activity`, {
        params: cleanParams({ filter, before, limit }),
      })
    ).data,
  mentionable: async (taskId) => (await axiosPrivate.get(`${BASE}/tasks/${taskId}/mentionable`)).data,
  /** Opening a task clears the caller's "new comments" alert. */
  markSeen: async (taskId) => (await axiosPrivate.post(`${BASE}/tasks/${taskId}/comments/seen`)).data,

  addComment: async (taskId, { body, mentions, clientRequestId, files = [] }, onUploadProgress) =>
    withUploadedFiles(taskId, files, onUploadProgress, async (meta) =>
      (
        await axiosPrivate.post(`${BASE}/tasks/${taskId}/comments`, {
          body,
          mentions: mentions || [],
          clientRequestId,
          files: meta,
        })
      ).data,
    ),
  editComment: async (commentId, body, mentions) =>
    (await axiosPrivate.patch(`${BASE}/comments/${commentId}`, { body, mentions })).data,
  deleteComment: async (commentId) => (await axiosPrivate.delete(`${BASE}/comments/${commentId}`)).data,

  attachments: async (taskId) => (await axiosPrivate.get(`${BASE}/tasks/${taskId}/attachments`)).data,
  uploadAttachments: async (taskId, files, onUploadProgress) =>
    withUploadedFiles(taskId, files, onUploadProgress, async (meta) =>
      (await axiosPrivate.post(`${BASE}/tasks/${taskId}/attachments`, { files: meta })).data,
    ),
  /** Soft-deletes the record, then removes the file from the bucket (best effort). */
  removeAttachment: async (attachment) => {
    const res = (await axiosPrivate.delete(`${BASE}/attachments/${attachment._id}`)).data;
    const path = bucketPath(attachment.url);
    if (path) deleteFile(IMAGES_BUCKET, path).catch(() => {});
    return res;
  },
};

const catalog = (resource) => ({
  list: async (params) =>
    (await axiosPrivate.get(`${BASE}/${resource}`, { params: cleanParams(params) })).data,
  get: async (id, params) =>
    (await axiosPrivate.get(`${BASE}/${resource}/${id}`, { params: cleanParams(params) })).data,
  create: async (body) => (await axiosPrivate.post(`${BASE}/${resource}`, body)).data,
  update: async (id, body) => (await axiosPrivate.patch(`${BASE}/${resource}/${id}`, body)).data,
  remove: async (id) => (await axiosPrivate.delete(`${BASE}/${resource}/${id}`)).data,
});

export const projectService = catalog("projects");

export const squadService = {
  ...catalog("squads"),
  addMember: async (squadId, employeeId, role) =>
    (await axiosPrivate.post(`${BASE}/squads/${squadId}/members`, { employeeId, role })).data,
  updateMemberRole: async (squadId, employeeId, role) =>
    (await axiosPrivate.patch(`${BASE}/squads/${squadId}/members/${employeeId}`, { role })).data,
  removeMember: async (squadId, employeeId) =>
    (await axiosPrivate.delete(`${BASE}/squads/${squadId}/members/${employeeId}`)).data,
};

export const sprintService = {
  ...catalog("sprints"),
  setMilestoneDone: async (sprintId, milestoneId, done) =>
    (await axiosPrivate.patch(`${BASE}/sprints/${sprintId}/milestones/${milestoneId}`, { done })).data,
  assignTasks: async (sprintId, taskIds) =>
    (await axiosPrivate.post(`${BASE}/sprints/${sprintId}/tasks`, { taskIds })).data,
  addAttachment: async (sprintId, attachment) =>
    (await axiosPrivate.post(`${BASE}/sprints/${sprintId}/attachments`, attachment)).data,
  removeAttachment: async (sprintId, attachmentId) =>
    (await axiosPrivate.delete(`${BASE}/sprints/${sprintId}/attachments/${attachmentId}`)).data,

  // ---- Sprint planning (all rules are enforced by the API) ----
  /** Create a sprint from Product Backlog tasks: { name, goal, description, startDate, endDate, tasks: [{ taskId, priority?, dueDate? }] }. */
  createFromBacklog: async (body) => (await axiosPrivate.post(`${BASE}/sprints/from-backlog`, body)).data,
  /** Unfinished tasks still linked to completed sprints (with their original sprint). */
  carryOver: async () => (await axiosPrivate.get(`${BASE}/sprints/carry-over`)).data,
  /** { tasks, hash, document }: the sprint's tasks at their due dates (a finished sprint keeps the set it ended with) and its saved timeline PDF. */
  timeline: async (sprintId, params) =>
    (await axiosPrivate.get(`${BASE}/sprints/${sprintId}/timeline`, { params: cleanParams(params) })).data,
  plan: async (sprintId) => (await axiosPrivate.get(`${BASE}/sprints/${sprintId}/plan`)).data,
  retrospective: async (sprintId) => (await axiosPrivate.get(`${BASE}/sprints/${sprintId}/retrospective`)).data,
  complete: async (sprintId, notes) =>
    (await axiosPrivate.post(`${BASE}/sprints/${sprintId}/complete`, notes)).data,
  updateRetrospective: async (sprintId, notes) =>
    (await axiosPrivate.patch(`${BASE}/sprints/${sprintId}/retrospective`, notes)).data,
  /** kind: "plan" | "retrospective". Replaces the previous document of that kind. */
  saveDocument: async (sprintId, kind, file) =>
    (await axiosPrivate.post(`${BASE}/sprints/${sprintId}/documents/${kind}`, file)).data,
};

/** Best human-readable message from an axios error. */
export const apiErrorMessage = (err, fallback = "Something went wrong") => {
  const data = err?.response?.data;
  if (data?.details?.length) return data.details.join(", ");
  return data?.message || err?.message || fallback;
};
